import React, { useState, useRef } from 'react';
import tarefaAnexoService from '../../services/tarefaAnexo.service';

// ════════════════════════════════════════════════════════════════════════════
// TarefaAnexos — r199
// ────────────────────────────────────────────────────────────────────────────
// Duas peças que andam juntas:
//   <AnexosCabecalho>  — a fileira de ícones dos anexos. Fica na barra inferior
//                        da Descrição, ao lado do "Expandir" (r199b — antes era
//                        no header azul). Passar o mouse mostra a DESCRIÇÃO do
//                        anexo em tooltip; clicar abre (imagem/PDF em aba nova,
//                        resto baixa).
//   <TarefaAnexosModal> — incluir / editar descrição / baixar / excluir.
//
// Tooltip é próprio (div posicionada) e não o `title` do HTML: o title só
// aparece depois de ~1s e o usuário acha que não tem nada ali.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul: '#003399', azulBg: '#eff6ff', borda: '#e8eaed', texto: '#1a1a2e',
  muted: '#888', branco: '#fff', cinzaBg: '#fafafa', vermelho: '#dc2626',
};

// Extensões aceitas — espelha o FORMATOS_PERMITIDOS do uploadTarefa.js.
// Se mudar lá, mude aqui (é só o filtro do seletor de arquivo do browser).
export const EXTENSOES_ACEITAS =
  '.jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.docx,.xlsx,.csv,.txt,.zip,.rar';

const LIMITE_MB = 25;

function formatarTamanho(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// Ícone por tipo — usa o mime e cai pra extensão (zip/rar chegam com mime
// genérico em vários browsers).
export function iconeDoAnexo(anexo) {
  const mime = (anexo.mime || '').toLowerCase();
  const nome = (anexo.nome_original || '').toLowerCase();
  if (mime.startsWith('image/'))                          return '🖼️';
  if (mime.includes('pdf')            || nome.endsWith('.pdf'))  return '📄';
  if (mime.includes('sheet')          || mime.includes('excel')
      || nome.endsWith('.xlsx')       || nome.endsWith('.csv'))  return '📊';
  if (mime.includes('word')           || nome.endsWith('.docx')) return '📝';
  if (nome.endsWith('.zip')           || nome.endsWith('.rar')
      || mime.includes('zip')         || mime.includes('rar'))   return '🗜️';
  if (mime.startsWith('text/')        || nome.endsWith('.txt'))  return '📃';
  return '📎';
}

// ════════════════════════════════════════════════════════════════════════════
// <AnexosCabecalho> — fileira de ícones dos anexos
// ────────────────────────────────────────────────────────────────────────────
// tema='claro'  → fundo branco do formulário (padrão, r199b)
// tema='escuro' → sobre o header azul
// ════════════════════════════════════════════════════════════════════════════
export function AnexosCabecalho({ anexos = [], onGerenciar, maxIcones = 8, tema = 'claro' }) {
  const [hover, setHover] = useState(null);   // id do anexo sob o mouse
  const [abrindo, setAbrindo] = useState(null);

  const escuro = tema === 'escuro';
  const visiveis = anexos.slice(0, maxIcones);
  const excedente = anexos.length - visiveis.length;

  const estiloBotao = (ativo) => ({
    width: '26px', height: '24px', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${escuro ? 'rgba(255,255,255,0.45)' : COR.borda}`,
    borderRadius: '4px',
    backgroundColor: escuro
      ? (ativo ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)')
      : (ativo ? COR.azulBg : COR.cinzaBg),
    cursor: 'pointer', fontSize: '12px', lineHeight: 1,
  });

  const abrir = async (anexo) => {
    setAbrindo(anexo.id);
    try {
      await tarefaAnexoService.abrir(anexo);
    } catch (err) {
      console.warn('Erro ao abrir anexo:', err);
    } finally {
      setAbrindo(null);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
      {visiveis.map(a => (
        <div key={a.id} style={{ position: 'relative' }}
             onMouseEnter={() => setHover(a.id)}
             onMouseLeave={() => setHover(null)}>
          <button type="button" onClick={() => abrir(a)} disabled={abrindo === a.id}
            style={{ ...estiloBotao(hover === a.id), cursor: abrindo === a.id ? 'wait' : 'pointer' }}>
            {iconeDoAnexo(a)}
          </button>

          {/* Tooltip com a descrição (r199 item 2) */}
          {hover === a.id && (
            <div style={{
              position: 'absolute', top: '28px', left: '50%', transform: 'translateX(-50%)',
              zIndex: 300, minWidth: '160px', maxWidth: '300px',
              backgroundColor: COR.texto, color: COR.branco,
              padding: '6px 9px', borderRadius: '5px',
              fontSize: '11px', lineHeight: '1.35',
              boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
              pointerEvents: 'none', whiteSpace: 'normal', wordBreak: 'break-word',
              fontFamily: "'Manrope', sans-serif", textAlign: 'left',
            }}>
              <div style={{ fontWeight: '700' }}>{a.descricao || a.nome_original}</div>
              <div style={{ opacity: 0.75, marginTop: '2px' }}>
                {a.nome_original}{a.tamanho_bytes ? ` · ${formatarTamanho(a.tamanho_bytes)}` : ''}
              </div>
            </div>
          )}
        </div>
      ))}

      {excedente > 0 && (
        <button type="button" onClick={onGerenciar} title={`Mais ${excedente} anexo(s)`}
          style={{
            ...estiloBotao(false),
            width: 'auto', padding: '0 6px',
            color: escuro ? COR.branco : COR.muted,
            fontSize: '10px', fontWeight: '700',
            fontFamily: "'Manrope', sans-serif",
          }}>+{excedente}</button>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <TarefaAnexosModal> — gerenciamento (incluir / descrever / baixar / excluir)
// ════════════════════════════════════════════════════════════════════════════
export function TarefaAnexosModal({ tarefaId, anexos = [], podeAlterar, onFechar, onMudou }) {
  const [arquivo, setArquivo]     = useState(null);
  const [descricao, setDescricao] = useState('');
  const [enviando, setEnviando]   = useState(false);
  const [erro, setErro]           = useState('');
  const [editando, setEditando]   = useState(null);   // { id, texto }
  const fileRef = useRef(null);

  const limparFormulario = () => {
    setArquivo(null);
    setDescricao('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const enviar = async () => {
    if (!arquivo) return setErro('Escolha um arquivo.');
    setEnviando(true);
    setErro('');
    try {
      await tarefaAnexoService.upload(tarefaId, arquivo, descricao);
      limparFormulario();
      if (onMudou) await onMudou();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Falha ao enviar o anexo.');
    } finally {
      setEnviando(false);
    }
  };

  const salvarDescricao = async () => {
    if (!editando) return;
    const texto = (editando.texto || '').trim();
    if (!texto) return setErro('A descrição não pode ser vazia.');
    try {
      setErro('');
      await tarefaAnexoService.atualizarDescricao(tarefaId, editando.id, texto);
      setEditando(null);
      if (onMudou) await onMudou();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Falha ao salvar a descrição.');
    }
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir o anexo "${a.nome_original}"?`)) return;
    try {
      setErro('');
      await tarefaAnexoService.excluir(tarefaId, a.id);
      if (onMudou) await onMudou();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Falha ao excluir o anexo.');
    }
  };

  const baixar = async (a) => {
    try {
      setErro('');
      await tarefaAnexoService.abrir(a);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Falha ao abrir o anexo.');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10050,
      backgroundColor: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{
        width: '860px', maxWidth: '96vw', maxHeight: '90vh',
        backgroundColor: COR.branco, borderRadius: '8px', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: COR.azul, color: COR.branco,
          padding: '12px 18px', fontSize: '15px', fontWeight: '700',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>📎 Anexos da tarefa #{tarefaId} ({anexos.length})</span>
          <button onClick={onFechar} title="Fechar" style={{
            background: 'transparent', border: 'none', color: COR.branco,
            fontSize: '22px', cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>

        {/* Lista */}
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
          {anexos.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '24px',
              color: COR.muted, fontSize: '12px', fontStyle: 'italic',
            }}>
              Nenhum anexo nesta tarefa.
            </div>
          ) : anexos.map(a => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: '8px 10px', marginBottom: '6px',
              backgroundColor: COR.cinzaBg,
              border: `1px solid ${COR.borda}`, borderRadius: '6px',
            }}>
              <span style={{ fontSize: '18px', lineHeight: '20px' }}>{iconeDoAnexo(a)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '12px', fontWeight: '700', color: COR.texto,
                  wordBreak: 'break-word',
                }}>{a.nome_original}</div>

                {editando && editando.id === a.id ? (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '5px' }}>
                    <input
                      value={editando.texto}
                      onChange={e => setEditando({ ...editando, texto: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') salvarDescricao(); }}
                      autoFocus
                      style={{
                        flex: 1, height: '28px', padding: '0 8px',
                        border: `1px solid ${COR.azul}`, borderRadius: '4px',
                        fontSize: '12px', fontFamily: "'Manrope', sans-serif",
                      }}
                    />
                    <button type="button" onClick={salvarDescricao} style={{
                      padding: '0 10px', height: '28px', fontSize: '11px', fontWeight: '700',
                      border: `1px solid ${COR.azul}`, borderRadius: '4px',
                      backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
                    }}>Salvar</button>
                    <button type="button" onClick={() => setEditando(null)} style={{
                      padding: '0 8px', height: '28px', fontSize: '11px',
                      border: `1px solid ${COR.borda}`, borderRadius: '4px',
                      backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
                    }}>×</button>
                  </div>
                ) : (
                  <div style={{
                    fontSize: '11px', color: COR.muted, marginTop: '2px',
                    wordBreak: 'break-word',
                  }}>
                    {a.descricao || <i>sem descrição</i>}
                    {podeAlterar && (
                      <button type="button"
                        onClick={() => setEditando({ id: a.id, texto: a.descricao || '' })}
                        title="Editar descrição"
                        style={{
                          border: 'none', background: 'transparent', color: COR.azul,
                          cursor: 'pointer', fontSize: '11px', padding: '0 4px',
                        }}>✎</button>
                    )}
                  </div>
                )}

                <div style={{ fontSize: '10px', color: COR.muted, marginTop: '3px' }}>
                  {formatarTamanho(a.tamanho_bytes)}
                  {a.criado_por_nome ? ` · ${a.criado_por_nome}` : ''}
                  {a.criado_em ? ` · ${new Date(a.criado_em).toLocaleString('pt-BR')}` : ''}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                <button type="button" onClick={() => baixar(a)} title="Abrir / baixar"
                  style={{
                    padding: '4px 8px', fontSize: '12px',
                    border: `1px solid ${COR.borda}`, borderRadius: '4px',
                    backgroundColor: COR.branco, cursor: 'pointer',
                  }}>⬇</button>
                {podeAlterar && (
                  <button type="button" onClick={() => excluir(a)} title="Excluir anexo"
                    style={{
                      padding: '4px 8px', fontSize: '12px',
                      border: `1px solid ${COR.borda}`, borderRadius: '4px',
                      backgroundColor: COR.branco, color: COR.vermelho, cursor: 'pointer',
                    }}>🗑</button>
                )}
              </div>
            </div>
          ))}

          {erro && (
            <div style={{
              padding: '6px 10px', marginTop: '8px',
              backgroundColor: '#fff0f0', border: `1px solid ${COR.vermelho}`,
              borderRadius: '4px', color: COR.vermelho, fontSize: '11px', fontWeight: '600',
            }}>{erro}</div>
          )}
        </div>

        {/* ─── Inclusão (r199b: empilhado, cada campo na largura toda) ─────── */}
        {podeAlterar && (
          <div style={{
            borderTop: `1px solid ${COR.borda}`, padding: '14px 18px',
            backgroundColor: COR.azulBg, flexShrink: 0,
          }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: COR.azul, marginBottom: '8px' }}>
              NOVO ANEXO
            </div>

            {/* Linha 1: arquivo — botão + nome do escolhido */}
            <input ref={fileRef} type="file" accept={EXTENSOES_ACEITAS}
              onChange={e => { setArquivo(e.target.files?.[0] || null); setErro(''); }}
              style={{ display: 'none' }} />
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '7px 10px', marginBottom: '8px',
              backgroundColor: COR.branco,
              border: `1px dashed ${arquivo ? COR.azul : COR.borda}`, borderRadius: '5px',
            }}>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={enviando}
                style={{
                  padding: '6px 14px', fontSize: '12px', fontWeight: '600',
                  border: `1px solid ${COR.borda}`, borderRadius: '5px',
                  backgroundColor: COR.cinzaBg, color: COR.texto,
                  cursor: enviando ? 'default' : 'pointer', whiteSpace: 'nowrap',
                  fontFamily: "'Manrope', sans-serif",
                }}>📁 Escolher arquivo</button>
              <span style={{
                flex: 1, minWidth: 0, fontSize: '12px',
                color: arquivo ? COR.texto : COR.muted,
                fontWeight: arquivo ? '600' : '400',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {arquivo
                  ? `${arquivo.name} (${formatarTamanho(arquivo.size)})`
                  : 'Nenhum arquivo selecionado'}
              </span>
              {arquivo && (
                <button type="button" onClick={limparFormulario} title="Limpar seleção"
                  style={{
                    border: 'none', background: 'transparent', color: COR.muted,
                    cursor: 'pointer', fontSize: '15px', padding: '0 2px', lineHeight: 1,
                  }}>×</button>
              )}
            </div>

            {/* Linha 2: descrição — largura inteira */}
            <label style={{
              display: 'block', fontSize: '11px', fontWeight: '600',
              color: COR.texto, marginBottom: '4px',
            }}>
              Descrição do conteúdo <span style={{ color: COR.muted, fontWeight: '400' }}>
                (é o que aparece no tooltip do ícone)
              </span>
            </label>
            <textarea
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              disabled={enviando}
              placeholder="Ex.: Print do erro na tela de faturamento — cliente enviou por e-mail em 20/08"
              style={{
                width: '100%', boxSizing: 'border-box',
                minHeight: '60px', maxHeight: '160px', padding: '8px 10px',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                fontSize: '12px', fontFamily: "'Manrope', sans-serif",
                lineHeight: '1.4', resize: 'vertical',
              }}
            />

            {/* Linha 3: limites à esquerda, ação à direita */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '12px', marginTop: '8px',
            }}>
              <div style={{ fontSize: '10px', color: COR.muted, lineHeight: '1.4' }}>
                Aceita imagens, PDF, docx, xlsx, csv, txt, zip e rar · até {LIMITE_MB} MB por arquivo.
                <br />Sem descrição, vale o nome do arquivo.
              </div>
              <button type="button" onClick={enviar} disabled={enviando || !arquivo}
                style={{
                  padding: '9px 22px', fontSize: '12px', fontWeight: '700',
                  border: `1px solid ${COR.azul}`, borderRadius: '5px',
                  backgroundColor: COR.azul, color: COR.branco,
                  cursor: enviando ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                  opacity: (!arquivo || enviando) ? 0.5 : 1,
                  fontFamily: "'Manrope', sans-serif",
                }}>{enviando ? 'Enviando...' : '+ Anexar'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TarefaAnexosModal;
